import React, { useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { RotateCw } from "lucide-react";
import { getPackageExchanges } from "../../api/packages";
import { canViewExchanges } from "../../utils/orderActions";
import { packageStatus } from "../../utils/packageStatus";

// Bloque "Cambio de producto" del detalle de pedido. Un pedido entregado puede
// tener pedidos hijo de cambio (envíos que llevan el reemplazo y retiran el
// original); el hijo, a su vez, puede tener los suyos. La relación es de
// microservice-packages y se lee con GET /packages/:id/exchanges.

// Lo que informó el repartidor al entregar el hijo (`exchangeReturn.outcome`).
const OUTCOME_LABELS = {
  received: "Producto devuelto",
  not_received: "El cliente no devolvió el producto",
  not_reported: "Sin informar (app anterior)",
};

const TERMINAL_STATUSES = [4, 5, 6, 7];

const pillTone = (status) => {
  if (status === 4 || status === 41) return "success";
  if (status === 5 || status === 6 || status === 7) return "error";
  if (status === 21 || status === 22 || status === 31) return "warning";
  return "neutral";
};

/**
 * true si el pedido participa de un cambio (es hijo o tiene hijos). Solo esos
 * piden la cadena: la gran mayoría de los pedidos no la tiene y no debe pagar
 * una lectura más al abrir el detalle.
 */
export const isExchangeRelated = (pkg) =>
  Boolean(pkg?.exchangeOf?.packageId) || (pkg?.exchanges?.childIds?.length ?? 0) > 0;

function StatusPill({ status }) {
  const { t } = useTranslation();
  if (status === null || status === undefined) return null;
  return (
    <span className={`pd-pill pd-pill--${pillTone(status)}`}>
      {t(packageStatus[status] || String(status))}
    </span>
  );
}

/** Resultado del retiro del producto original de un hijo, con sus fotos. */
function ReturnOutcome({ exchangeReturn, status, onOpenPhotos }) {
  const { t } = useTranslation();
  const label = OUTCOME_LABELS[exchangeReturn?.outcome];
  if (!label) {
    // Sin resultado y todavía en curso: lo que va a pasar al entregarlo.
    return TERMINAL_STATUSES.includes(status) ? null : (
      <span className="pd-timeline__meta">
        {t("Al entregar, el repartidor retira el producto original.")}
      </span>
    );
  }
  const images = exchangeReturn.outcome === "received" && Array.isArray(exchangeReturn.images)
    ? exchangeReturn.images.filter(Boolean)
    : [];
  return (
    <>
      <span className="pd-timeline__meta">{t(label)}</span>
      {images.length > 0 && (
        <div className="pd-timeline__actions">
          {images.map((src, index) => (
            <button
              key={src}
              type="button"
              className="pd-thumb"
              onClick={() => onOpenPhotos?.(images, index)}
              aria-label={t("Ampliar foto del producto devuelto {{current}} de {{total}}", {
                current: index + 1,
                total: images.length,
              })}
            >
              <img src={src} alt={t("Producto devuelto {{n}}", { n: index + 1 })} />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * @param {object} props
 * @param {object} props.pkg - pedido del detalle (padre, hijo o ambos)
 * @param {string} [props.role] - _id del rol del usuario
 * @param {(images: string[], index: number) => void} [props.onOpenPhotos]
 */
export default function ExchangeDetails({ pkg, role, onOpenPhotos }) {
  const { t } = useTranslation();
  const packageId = pkg?._id;
  const related = isExchangeRelated(pkg);
  // Holding, superadmin del sistema y demás roles fuera de la lista blanca reciben
  // 404 por diseño: no se pide la cadena y se muestra lo que ya trae el pedido.
  const canFetch = canViewExchanges(role);
  // Al crear un cambio el detalle se recarga y `childIds` crece: vuelve a leer.
  const childCount = pkg?.exchanges?.childIds?.length ?? 0;
  const [attempt, setAttempt] = useState(0);
  // status: "loading" | "loaded" | "unavailable" (sin acceso a la cadena) | "failed"
  const [chain, setChain] = useState(() => ({
    status: canFetch ? "loading" : "unavailable",
    data: null,
  }));

  useEffect(() => {
    if (!packageId || !related) return undefined;
    if (!canFetch) {
      setChain({ status: "unavailable", data: null });
      return undefined;
    }
    let alive = true;
    setChain({ status: "loading", data: null });
    getPackageExchanges(packageId).then((res) => {
      if (!alive) return;
      if (res.success) {
        setChain({ status: "loaded", data: res.data });
        return;
      }
      // 404 es la respuesta anti-enumeración a quien no es dueño: no se arregla
      // reintentando, así que no se ofrece "Reintentar".
      setChain({ status: res.error?.status === 404 ? "unavailable" : "failed", data: null });
    });
    return () => {
      alive = false;
    };
  }, [packageId, related, canFetch, childCount, attempt]);

  if (!related) return null;

  const isChild = Boolean(pkg.exchangeOf?.packageId);
  const parent = chain.data?.parent ?? null;
  const children = Array.isArray(chain.data?.children) ? chain.data.children : [];
  // Sin acceso al padre (o mientras carga) el número igual se conoce: el hijo lo
  // guarda en `exchangeOf` justamente para mostrarlo sin otra lectura.
  const parentNumber = parent?.followingNumber ?? pkg.exchangeOf?.followingNumber;
  // Sin la cadena, de los hijos solo se sabe cuántos son (`exchanges.childIds`).
  const showChildCount = chain.status === "unavailable" && childCount > 0;
  // Etiqueta de devolución del mismo padre: se excluye con el cambio, pero puede
  // haber quedado una de antes (o de una carrera entre dos usuarios).
  const returnLabel = chain.data?.returnLabel ?? null;

  return (
    <div>
      <ol className="pd-timeline">
        {isChild && (
          <li className="pd-timeline__item">
            {(children.length > 0 || showChildCount || returnLabel) && <span className="pd-timeline__line" />}
            <span className="pd-timeline__dot pd-timeline__dot--brand" />
            <div className="pd-timeline__body">
              <div className="pd-timeline__head">
                <span className="pd-timeline__status">
                  {t("Cambio del pedido #{{number}}", { number: parentNumber })}
                </span>
                <StatusPill status={parent?.status} />
              </div>
              <ReturnOutcome
                exchangeReturn={pkg.exchangeReturn}
                status={pkg.status}
                onOpenPhotos={onOpenPhotos}
              />
            </div>
          </li>
        )}
        {children.map((child, index) => (
          <li key={child._id || index} className="pd-timeline__item">
            {(index < children.length - 1 || returnLabel) && <span className="pd-timeline__line" />}
            <span className={`pd-timeline__dot pd-timeline__dot--${child.status === 4 ? "success" : "brand"}`} />
            <div className="pd-timeline__body">
              <div className="pd-timeline__head">
                <span className="pd-timeline__status">
                  {t("Cambio #{{number}}", { number: child.followingNumber })}
                </span>
                <StatusPill status={child.status} />
              </div>
              {child.createdAt && (
                <span className="pd-timeline__meta">
                  {t("Creado el {{date}}", { date: moment(child.createdAt).format("DD/MM/YY HH:mm") })}
                </span>
              )}
              <ReturnOutcome
                exchangeReturn={child.exchangeReturn}
                status={child.status}
                onOpenPhotos={onOpenPhotos}
              />
            </div>
          </li>
        ))}
        {returnLabel && (
          <li className="pd-timeline__item">
            <span className="pd-timeline__dot pd-timeline__dot--brand" />
            <div className="pd-timeline__body">
              <div className="pd-timeline__head">
                <span className="pd-timeline__status">
                  {t("Etiqueta de devolución #{{number}}", { number: returnLabel.followingNumber })}
                </span>
                <StatusPill status={returnLabel.status} />
              </div>
            </div>
          </li>
        )}
        {showChildCount && (
          <li className="pd-timeline__item">
            <span className="pd-timeline__dot pd-timeline__dot--brand" />
            <div className="pd-timeline__body">
              <div className="pd-timeline__head">
                <span className="pd-timeline__status">
                  {childCount === 1
                    ? t("Tiene 1 cambio")
                    : t("Tiene {{count}} cambios", { count: childCount })}
                </span>
              </div>
            </div>
          </li>
        )}
      </ol>

      {chain.status === "loading" && <p className="pd-muted mb-0">{t("Cargando cambios…")}</p>}
      {chain.status === "loaded" && !isChild && children.length === 0 && (
        <p className="pd-muted mb-0">{t("Sin cambios para mostrar.")}</p>
      )}
      {chain.status === "failed" && (
        <div className="flex items-center gap-2 flex-wrap">
          <p className="pd-muted mb-0">{t("No se pudo cargar el estado del cambio.")}</p>
          <button type="button" className="pd-btn-ghost" onClick={() => setAttempt((n) => n + 1)}>
            <RotateCw size={14} aria-hidden="true" />
            {t("Reintentar")}
          </button>
        </div>
      )}
    </div>
  );
}
